import React, { useState } from 'react';
import { Button } from './button';
import { Dialog, DialogTitle, DialogBody } from './dialog';
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase';
import { logBitacora } from '../utils/bitacora';
import { parseXmlFianza, parseXmlFactura } from '../utils/xmlParsers';
import { useTarifas } from '../contexts/DataContext';
import CompactFileReceiver from './CompactFileReceiver';
import {
  ESTADO_FIANZA,
  EVENTO_TIPOS,
  createEmisionEvento,
  createEmisionFactura,
  buildEvento,
  applyEndoso,
} from '../utils/fianzaEventos';
import {
  calculateTarifa,
  calculateComision,
  getPrimaAmount,
  getEmisorNombre,
  getEmisorRfc,
  getSubRamo,
  getTipoFianza,
  toTimestamp,
  partitionTarifas,
} from '../utils/calculations';
import { recordComisionEvento } from '../api/comisiones';
import { fianzaStoragePath } from '../utils/contratoStorage';

// Same file-drop row SustituirFianzaDialog uses.
function FileSlot({ label, type, file, onFile, disabled }) {
  return (
    <div>
      <div className="text-xs font-medium text-azul-surelia mb-1">{label}</div>
      {file ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-cian-digital/40 bg-cian-digital/5 px-3 py-2">
          <span className="text-xs text-gray-700 truncate">{file.name}</span>
          <button type="button" onClick={() => onFile(null)} disabled={disabled} className="text-gray-400 hover:text-red-500 shrink-0">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <CompactFileReceiver
          type={type}
          disabled={disabled}
          onFilesChange={(files) => onFile(files?.[0] || null)}
          placeholder={type === 'xml' ? 'Arrastra o elige XML' : 'Arrastra o elige PDF'}
        />
      )}
    </div>
  );
}

/**
 * RenovarFianzaDialog — renews a renovable fianza with a NEW fianza entity.
 *
 * Unlike a sustitución, a renovación ends nothing: the old fianza ran (or is
 * running out) its full term, KEEPS its estado and its comisión — no reversal.
 * A `renovacion` event is appended to the old fianza linking it to its
 * successor (`renovadaPor`); the new fianza is created active with `renuevaA`,
 * its own factura, and its own emisión movement in the comisión ledger. The
 * chain of renovaciones IS the fianza's history across periods.
 *
 * Both fianzas + the new emisión are written atomically through the backend.
 */
export function RenovarFianzaDialog({ isOpen, onClose, fianza, contratoData, clientData, user, userData, onApplied, buildBitacoraMeta }) {
  const { tarifas } = useTarifas();
  const createdBy = { uid: user?.uid || null, nombre: userData?.nombre || userData?.correo || null };

  const [fianzaXmlFile, setFianzaXmlFile] = useState(null);
  const [facturaXmlFile, setFacturaXmlFile] = useState(null);
  const [fianzaPdfFile, setFianzaPdfFile] = useState(null);
  const [facturaPdfFile, setFacturaPdfFile] = useState(null);
  const [parsedFianza, setParsedFianza] = useState(null);
  const [parsedFactura, setParsedFactura] = useState(null);
  const [nota, setNota] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const resetAndClose = () => {
    setFianzaXmlFile(null); setFacturaXmlFile(null);
    setFianzaPdfFile(null); setFacturaPdfFile(null);
    setParsedFianza(null); setParsedFactura(null);
    setNota(''); setError(null);
    onClose();
  };

  const onFianzaXml = async (file) => {
    if (!file) { setFianzaXmlFile(null); setParsedFianza(null); return; }
    setError(null);
    const res = await parseXmlFianza(file);
    if (!res.success) { setError(`XML de fianza inválido: ${res.error}`); return; }
    setParsedFianza(res.data); setFianzaXmlFile(file);
  };
  const onFacturaXml = async (file) => {
    if (!file) { setFacturaXmlFile(null); setParsedFactura(null); return; }
    setError(null);
    const res = await parseXmlFactura(file);
    if (!res.success) { setError(`XML de factura inválido: ${res.error}`); return; }
    setParsedFactura(res.data); setFacturaXmlFile(file);
  };

  const handleSubmit = async () => {
    if (!parsedFianza && !parsedFactura) {
      setError('Sube al menos el XML de la nueva fianza o de su factura.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const ts = Date.now();
      const newFianzaId = `fianza_${ts}`;
      const basePath = fianzaStoragePath(contratoData, clientData?.id, newFianzaId);

      // Upload the renewed fianza's files.
      const filePaths = {};
      const uploads = [];
      const pushUpload = (file, name, key) => {
        if (!file) return;
        const p = `${basePath}/${name}`;
        uploads.push(uploadBytes(ref(storage, p), file));
        filePaths[key] = p;
      };
      pushUpload(fianzaXmlFile, 'fianza.xml', 'fianzaXml');
      pushUpload(facturaXmlFile, 'factura.xml', 'facturaXml');
      pushUpload(fianzaPdfFile, 'fianza.pdf', 'fianzaPdf');
      pushUpload(facturaPdfFile, 'factura.pdf', 'facturaPdf');
      await Promise.all(uploads);

      // The renewed fianza: same shape as UploadFianza, linked back via renuevaA.
      const nuevaFianza = {
        id: newFianzaId,
        contratoId: contratoData.id,
        createdAt: ts,
        filePaths,
        xmlFianza: parsedFianza || null,
        xmlFactura: parsedFactura || null,
        numeroFianza: parsedFianza?.numeroFianza || parsedFactura?.noIdentificacion || null,
        montoAfianzado: parsedFianza?.montoAfianzado || null,
        monto: parsedFactura?.total ?? null,
        tipoFianza: parsedFianza?.tipo || fianza?.tipoFianza || null,
        inicioVigenciaFianza: parsedFianza?.inicioVigenciaFianza || null,
        finVigenciaFianza: parsedFianza?.finVigenciaFianza || null,
        pagada: false,
        // A renewal of a renovable fianza is, until told otherwise, renovable too.
        renovable: true,
        // The convenio-split context survives the renewal.
        beneficiarioConvenio: Boolean(fianza?.beneficiarioConvenio),
        estadoFianza: ESTADO_FIANZA.ACTIVA,
        renuevaA: fianza.id,
      };
      // Lock the comisión % for the new period. Convenio fianzas inherit the
      // old fianza's captured % (the fiado agent's deal — the org's tarifa
      // tables never apply); when unknown, null lets the backend estimate from
      // the convenio-scope tables or book pct-pending.
      if (contratoData?.convenioId) {
        nuevaFianza.comisionPct = fianza?.comisionPct ?? null;
        // Same convenio, same other agent, same deal: a % a person confirmed
        // carries its confirmation into the new period.
        if (fianza?.comisionPctConfirmada && nuevaFianza.comisionPct != null) {
          nuevaFianza.comisionPctConfirmada = fianza.comisionPctConfirmada;
        }
      } else {
        const prima = getPrimaAmount(nuevaFianza);
        const montoAf = Number(nuevaFianza.montoAfianzado) || 0;
        const tarifa = calculateTarifa(prima, montoAf, {
          truncate: true,
          vigencia: {
            inicio: toTimestamp(nuevaFianza.inicioVigenciaFianza),
            fin: toTimestamp(nuevaFianza.finVigenciaFianza),
          },
        });
        const { comisionPct, pctEstimado } = calculateComision(
          prima, tarifa, partitionTarifas(tarifas).propias,
          getEmisorNombre(nuevaFianza), getSubRamo(nuevaFianza), getTipoFianza(nuevaFianza),
          getEmisorRfc(nuevaFianza)
        );
        // A Tabla-general % is an estimate — leave it unlocked so the backend
        // books it pctEstimado ("por confirmar").
        nuevaFianza.comisionPct = pctEstimado ? null : comisionPct ?? null;
      }
      nuevaFianza.eventos = [createEmisionEvento(nuevaFianza)];
      nuevaFianza.facturas = [createEmisionFactura(nuevaFianza)];

      // NON-terminal renovacion event on the old fianza: estado unchanged,
      // comisión kept, linked to its successor.
      const evento = buildEvento({
        tipo: EVENTO_TIPOS.RENOVACION,
        origen: 'fianza',
        createdBy,
        nota: nota.trim() || null,
        renovadaPorFianzaId: newFianzaId,
        ts,
      });
      const nextOld = applyEndoso(fianza, evento, { renovadaPor: newFianzaId });

      // Write both fianzas + the new emisión movement atomically. No reversal:
      // the backend's renovacion branch books only the new fianza's emisión.
      await recordComisionEvento({
        contractId: contratoData.id,
        fianzaId: fianza.id,
        eventoTipo: 'renovacion',
        eventoId: evento.id,
        nextFianza: nextOld,
        nuevaFianza,
        fecha: ts,
      });

      try {
        await logBitacora({
          action: 'UPDATE',
          collection: 'contracts',
          documentId: contratoData.id,
          user,
          userData,
          metadata: buildBitacoraMeta
            ? buildBitacoraMeta(`Renovó fianza #${fianza.numeroFianza || 'sin número'}`, { fianzaId: fianza.id, renovadaPor: newFianzaId })
            : { contratoId: contratoData.id, fianzaId: fianza.id, description: `Renovó fianza #${fianza.numeroFianza || 'sin número'}`, renovadaPor: newFianzaId },
        });
      } catch (e) { console.error('bitacora renovacion failed', e); }

      onApplied?.();
      resetAndClose();
    } catch (e) {
      console.error('renovacion failed', e);
      setError(e.message || 'No se pudo renovar la fianza.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={submitting ? () => {} : resetAndClose} size="2xl">
      <DialogTitle><span className="text-azul-surelia">Renovar fianza</span></DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          <div className="rounded-lg bg-cian-digital/10 border border-cian-digital/30 px-3 py-2 text-xs text-azul-surelia">
            Se creará una <span className="font-semibold">nueva fianza</span> para el siguiente periodo, ligada a la actual como su renovación. La fianza actual conserva su historial y su comisión — no se revierte nada. Sube los documentos del nuevo periodo.
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-3">
              <ArrowPathIcon className="w-4 h-4 text-azul-surelia" />
              <h4 className="text-sm font-semibold text-gray-800">Documentos de la renovación</h4>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FileSlot label="XML fianza" type="xml" file={fianzaXmlFile} onFile={onFianzaXml} disabled={submitting} />
              <FileSlot label="PDF fianza" type="pdf" file={fianzaPdfFile} onFile={setFianzaPdfFile} disabled={submitting} />
              <FileSlot label="XML factura" type="xml" file={facturaXmlFile} onFile={onFacturaXml} disabled={submitting} />
              <FileSlot label="PDF factura" type="pdf" file={facturaPdfFile} onFile={setFacturaPdfFile} disabled={submitting} />
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Nota (opcional)</label>
            <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia"
              placeholder="Notas de la renovación…" />
          </div>

          {error && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>}

          <div className="flex justify-end gap-2 pt-1">
            <Button plain onClick={resetAndClose} disabled={submitting}>Cancelar</Button>
            <Button color="surelia" onClick={handleSubmit} disabled={submitting || (!parsedFianza && !parsedFactura)}>
              {submitting ? 'Renovando…' : 'Renovar fianza'}
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default RenovarFianzaDialog;
